import { AxiosRequestConfig } from 'axios';

import {
  AnyObject,
  Pagination,
  Params,
  PlatformApp,
  PlatformAppInstance,
  WithPagination,
} from '@typings';
import { API_APPS, API_APPS_V2, JWT_TOKEN } from '@constants';
import { axios } from '@features';
import { getFormattedApiUrl, localStorage, path } from '@utils';

/**
 * Helper
 */
const getApiUrl = (context: Params.Context) =>
  getFormattedApiUrl({ ...context, apiUrl: API_APPS, plural: false });

const processAppsMonitoringUrl = (url: string) => url.replace('jobs', 'apps');

export const getPlatformAppsTemplates = async ({
  formatResponse = false,
  ...params
}: Params.GetPlatformAppsTemplates = {}): Promise<
  Pagination.Response<PlatformApp.Template>
> => {
  return axios.get<never, WithPagination<PlatformApp.Template>>(
    path.create(API_APPS_V2, 'templates'),
    {
      params,
      paramsSerializer: { indexes: null },
      formatResponse,
    },
  );
};

export const getPlatformAppsTemplate = (
  {
    clusterName,
    organizationName,
    projectName,
    version,
    name,
  }: Params.GetPlatformAppsTemplate,
  options?: AxiosRequestConfig,
) => {
  const url = getApiUrl({ clusterName, organizationName, projectName });

  return axios.get<never, PlatformApp.Template>(
    path.create(url, 'templates', name, version),
    options,
  );
};

export const getPlatformAppsInstances = ({
  clusterName,
  organizationName,
  projectName,
}: Params.GetPlatformAppsInstances) => {
  return axios.get<never, WithPagination<PlatformApp.Instance>>(
    path.create(API_APPS_V2, 'instances'),
    {
      params: {
        org: organizationName,
        project: projectName,
        cluster: clusterName,
        size: 100,
      },
    },
  );
};

export const getPlatformAppsInstance = ({
  instanceId,
}: Params.GetPlatformAppsInstance) => {
  return axios.get<never, PlatformApp.Instance>(
    path.create(API_APPS_V2, 'instances', instanceId),
  );
};

export const installPlatformApp = ({
  clusterName,
  organizationName,
  projectName,
  payload,
}: Params.InstallPlatformApp) => {
  const url = getApiUrl({ clusterName, organizationName, projectName });

  return axios.post<never, PlatformApp.Instance>(
    path.create(url, 'instances'),
    payload,
  );
};

export const uninstallPlatformApp = ({
  clusterName,
  organizationName,
  projectName,
  id,
}: Params.UninstallPlatformApp) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.delete<never>(path.create(url, 'instances', id));
};

export const getPlatformAppVersions = ({
  organizationName,
  clusterName,
  projectName,
  appName,
}: Params.GetPlatformAppVersions) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, PlatformApp.Template[]>(
    path.create(url, 'templates', appName),
    { formatResponse: false },
  );
};

export const getPlatformAppLog = ({
  instanceId,
  monitoringUrl,
  since = '',
}: Params.GetPlatformAppLog) => {
  const token = localStorage.get(JWT_TOKEN);
  const params = new URLSearchParams({
    since,
  });

  return fetch(
    path.create(
      processAppsMonitoringUrl(monitoringUrl),
      instanceId,
      `log?${params.toString()}`,
    ),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
      },
    },
  );
};

export const getPlatformAppLogContainers = ({
  instanceId,
  monitoringUrl,
  since,
}: Params.GetPlatformAppLogContainers) => {
  type Container = string;
  return axios.get<never, Container[]>(
    path.create(
      processAppsMonitoringUrl(monitoringUrl),
      instanceId,
      'containers',
    ),
    { params: { since } },
  );
};

export const getPlatformAppInstnceParams = (
  {
    organizationName,
    clusterName,
    projectName,
    instanceId,
    type,
  }: Params.GetPlatformAppInstnceParams,
  restoreRef?: boolean,
) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, AnyObject>(
    path.create(url, 'instances', instanceId, type),
    {
      formatResponse: false,
      sentryIgnoreCodes: 404,
      params: { restore_ref: restoreRef },
    },
  );
};

export const getPlatformAppIntegrationValues = ({
  organizationName,
  clusterName,
  projectName,
  typeName,
  page,
}: Params.GetPlatformAppIntegrationValues) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, WithPagination<PlatformAppInstance.Value>>(
    path.create(url, 'instances', 'values'),
    {
      params: { type: typeName, page, restore_ref: true },
      formatResponseLevel: 2,
    },
  );
};

export const getPlatformAppInstanceIntegrationValues = ({
  organizationName,
  clusterName,
  projectName,
  instanceId,
  page,
}: Params.GetPlatformAppInstanceIntegrationValues) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, WithPagination<PlatformAppInstance.Value>>(
    path.create(url, 'instances', instanceId, 'values'),
    {
      params: { page },
      formatResponseLevel: 2,
    },
  );
};

export const getPlatformAppInstanceEvents = ({
  organizationName,
  clusterName,
  projectName,
  instanceId,
}: Params.GetPlatformAppInstanceEvents) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, WithPagination<PlatformAppInstance.Event>>(
    path.create(url, 'instances', instanceId, 'events'),
  );
};

export const getPlatformAppInstanceUrls = ({
  organizationName,
  clusterName,
  projectName,
  instanceId,
}: Params.GetPlatformAppInstanceUrls) => {
  const url = getApiUrl({
    clusterName,
    organizationName,
    projectName,
  });

  return axios.get<never, { data: PlatformAppInstance.Url[] }>(
    path.create(url, 'instances', instanceId, 'endpoints'),
  );
};

export const getPlatformAppsTemplatesTags = () => {
  return axios.get<never, PlatformApp.Template['tags']>(
    path.create(API_APPS_V2, 'templates', 'tags'),
  );
};
